import { useState, useEffect, useMemo } from 'react';
import { WeatherInfo, HourlyWeather } from '../types';
import { DESTINATIONS } from '../lib/constants';
import { getDayName, getDayNumber, getFormattedDate } from '../lib/weather';
import { weatherApi, WeatherDayForecast } from '../lib/api';
import { getChileNow } from '../lib/date';

interface UseWeatherProps {
  selectedRouteId: string;
  tripDate: string;
  onDateChange?: (date: string) => void;
  enabled?: boolean;
}

/**
 * Hook para manejar clima - usa API FastAPI
 */
export const useWeather = ({ selectedRouteId, tripDate, onDateChange, enabled = true }: UseWeatherProps) => {
  const [weatherForecast, setWeatherForecast] = useState<WeatherInfo[]>([]);
  const [loadingWeather, setLoadingWeather] = useState(false);
  const [selectedWeatherIndex, setSelectedWeatherIndex] = useState<number>(0);
  const FORECAST_DAYS = 7;

  useEffect(() => {
    if (!enabled) return;

    const fetchWeather = async () => {
      setLoadingWeather(true);
      try {
        const dest = DESTINATIONS[selectedRouteId];
        if (!dest) return;

        const today = getChileNow().date;
        const startDate = getWindowStartDate(tripDate, today, 3);

        // Open-Meteo entrega hasta ~16 días. Si la fecha está fuera de horizonte,
        // mostramos fallback local con ventana alrededor de la fecha seleccionada.
        if (isOutsideForecastHorizon(tripDate, 15)) {
          const fallback = buildFallbackForecast(startDate, FORECAST_DAYS);
          setWeatherForecast(fallback);
          const idxFallback = fallback.findIndex(d => d.rawDate === tripDate);
          setSelectedWeatherIndex(idxFallback !== -1 ? idxFallback : 0);
          return;
        }

        const response = await weatherApi.getForecast(dest.lat, dest.lon, FORECAST_DAYS, startDate);

        const newForecast: WeatherInfo[] = response.days.map((day: WeatherDayForecast) => {
          const hourly: HourlyWeather[] = day.hourly.map(h => ({
            hour: h.hour,
            temp: Math.round(h.temp),
            precipProb: h.precipProb,
            weatherCode: h.weatherCode,
          }));

          return {
            day: getDayName(day.date),
            date: getDayNumber(day.date),
            maxTemp: Math.round(day.maxTemp),
            minTemp: Math.round(day.minTemp),
            avgTemp: Math.round(day.avgTemp),
            condition: day.condition,
            fullDate: getFormattedDate(day.date),
            rawDate: day.date,
            hourly,
          };
        });

        if (newForecast.length === 0) {
          const fallback = buildFallbackForecast(startDate, FORECAST_DAYS);
          setWeatherForecast(fallback);
          const idxFallback = fallback.findIndex(d => d.rawDate === tripDate);
          setSelectedWeatherIndex(idxFallback !== -1 ? idxFallback : 0);
          return;
        }

        setWeatherForecast(newForecast);

        const idx = newForecast.findIndex(d => d.rawDate === tripDate);
        if (idx !== -1) {
          setSelectedWeatherIndex(idx);
        } else if (newForecast.length > 0) {
          // Mantener la fecha seleccionada por usuario aunque no exista en pronóstico
          setSelectedWeatherIndex(0);
        }
      } catch {
        const today = getChileNow().date;
        const fallback = buildFallbackForecast(getWindowStartDate(tripDate, today, 3), FORECAST_DAYS);
        setWeatherForecast(fallback);
        const idxFallback = fallback.findIndex(d => d.rawDate === tripDate);
        setSelectedWeatherIndex(idxFallback !== -1 ? idxFallback : 0);
      } finally {
        setLoadingWeather(false);
      }
    };

    fetchWeather();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, selectedRouteId, tripDate]);

  useEffect(() => {
    if (!enabled || weatherForecast.length === 0) return;
    const idx = weatherForecast.findIndex(d => d.rawDate === tripDate);
    if (idx !== -1 && idx !== selectedWeatherIndex) {
      setSelectedWeatherIndex(idx);
    }
  }, [enabled, tripDate, weatherForecast, selectedWeatherIndex]);

  const currentDayHourlyWeather = useMemo(() => {
    const byDate = weatherForecast.find(d => d.rawDate === tripDate);
    if (byDate) return byDate.hourly;
    return [];
  }, [weatherForecast, tripDate]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const visibleWeatherDays = useMemo(() => {
    if (weatherForecast.length === 0) return [];
    const windowSize = isMobile ? 3 : 7;
    const centerSlot = isMobile ? 1 : 3; // 0-based: casilla 2 en mobile, casilla 4 en desktop
    const today = getChileNow().date;
    // Ventana centrada en la fecha seleccionada, pero nunca antes de hoy — no se puede
    // registrar un viaje en el pasado. Mismo helper que usa el fetch de clima (línea ~34).
    const windowStart = toLocalDate(getWindowStartDate(tripDate, today, centerSlot));
    const mapByDate = new Map(weatherForecast.map((d) => [d.rawDate, d] as const));

    const result: WeatherInfo[] = [];
    for (let offset = 0; offset < windowSize; offset++) {
      const d = new Date(windowStart);
      d.setDate(d.getDate() + offset);
      const key = toYmd(d);
      const found = mapByDate.get(key);
      if (found) {
        result.push(found);
        continue;
      }
      const fallbackBase = weatherForecast.find((w) => w.rawDate === tripDate) || weatherForecast[0];
      result.push(buildSyntheticDayFromBase(fallbackBase, key));
    }

    return result;
  }, [weatherForecast, isMobile, tripDate]);

  return {
    weatherForecast,
    loadingWeather,
    selectedWeatherIndex,
    setSelectedWeatherIndex,
    currentDayHourlyWeather,
    visibleWeatherDays,
  };
};

const buildFallbackForecast = (baseDate: string, days: number): WeatherInfo[] => {
  const start = toLocalDate(baseDate);
  return Array.from({ length: days }, (_, index) => {
    const date = new Date(start);
    date.setDate(date.getDate() + index);
    const rawDate = toYmd(date);
    const minTemp = 12 + (index % 3);
    const maxTemp = 23 + (index % 4);
    const avgTemp = Math.round((minTemp + maxTemp) / 2);
    const hourly: HourlyWeather[] = Array.from({ length: 24 }, (_, h) => ({
      hour: h,
      temp: h >= 6 && h <= 18 ? avgTemp + 1 : avgTemp - 2,
      precipProb: 0,
      weatherCode: 1,
    }));

    return {
      day: getDayName(rawDate),
      date: getDayNumber(rawDate),
      maxTemp,
      minTemp,
      avgTemp,
      condition: 'sunny',
      fullDate: getFormattedDate(rawDate),
      rawDate,
      hourly,
    };
  });
};

const toYmd = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const toLocalDate = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

const isOutsideForecastHorizon = (ymd: string, maxDaysAhead: number): boolean => {
  const chileToday = getChileNow().date;
  if (ymd < chileToday) return true;
  const target = toLocalDate(ymd);
  const todayLocal = toLocalDate(chileToday);
  const diffMs = target.getTime() - todayLocal.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  return diffDays > maxDaysAhead;
};

const getWindowStartDate = (tripDate: string, minDate: string, daysBefore: number): string => {
  const target = toLocalDate(tripDate);
  target.setDate(target.getDate() - daysBefore);
  const min = toLocalDate(minDate);
  if (target.getTime() < min.getTime()) return minDate;
  return toYmd(target);
};

const buildSyntheticDayFromBase = (base: WeatherInfo, rawDate: string): WeatherInfo => {
  return {
    ...base,
    day: getDayName(rawDate),
    date: getDayNumber(rawDate),
    fullDate: getFormattedDate(rawDate),
    rawDate,
  };
};
